import api from '../utils/api';

/** An additional barn contact (barn manager, trainer, etc.). */
export interface BarnContact {
  name: string;
  role?: string;
  phone?: string;
  email?: string;
}

export interface Barn {
  id: string;
  name: string;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contacts?: BarnContact[] | null;
  notes?: string | null;
  isArchived: boolean;
  archivedAt?: string | null;
  clinicId: string;
  createdAt: string;
  updatedAt: string;
  // List enrichment
  horseCount?: number;
  clientCount?: number;
}

export interface BarnOwnerLink {
  clientId: string;
  name: string;
  ownershipPercentage: number | null;
}

export interface BarnRosterHorse {
  id: string;
  name: string;
  patientId?: string | null;
  breed: string;
  secondBreed?: string | null;
  isMixed: boolean;
  status: string;
  owners: Array<{ name: string; email?: string; phone?: string }>;
  location?: string | null;
  medicalAlerts?: Array<{
    id: string;
    text: string;
    isCritical?: boolean;
    createdBy?: string;
    createdAt?: string;
  }> | null;
  ownerLinks: BarnOwnerLink[];
}

export interface BarnClient {
  clientId: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  horseNames: string[];
}

export interface BarnDetail extends Barn {
  horses: BarnRosterHorse[];
  clients: BarnClient[];
}

export interface BarnFormData {
  name: string;
  // `null` on update = explicit clear (an OMITTED key means "leave unchanged"
  // to the API); create payloads simply drop empty fields.
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contacts?: BarnContact[];
  notes?: string | null;
}

export const barnsService = {
  list: async (includeArchived = false): Promise<Barn[]> => {
    const response = await api.get(`/barns${includeArchived ? '?includeArchived=true' : ''}`);
    return response.data;
  },

  get: async (id: string): Promise<BarnDetail> => {
    const response = await api.get(`/barns/${id}`);
    return response.data;
  },

  create: async (data: BarnFormData): Promise<Barn> => {
    const response = await api.post('/barns', data);
    return response.data;
  },

  update: async (id: string, data: Partial<BarnFormData>): Promise<Barn> => {
    const response = await api.put(`/barns/${id}`, data);
    return response.data;
  },

  archive: async (id: string): Promise<Barn> => {
    const response = await api.delete(`/barns/${id}`);
    return response.data;
  },

  restore: async (id: string): Promise<Barn> => {
    const response = await api.post(`/barns/${id}/restore`);
    return response.data;
  },
};
